import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { appSettings } from 'app/appSettings';
import {
  CalendarDay,
  CalendarFlorderInfo,
  CalendarWeek,
  DatesWithFlorderInfo
} from 'app/core/store/calendar/common/calendar.interface';
import { AvailablePickingDates } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import moment from 'moment';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-calendar-month',
  templateUrl: './calendar-month.component.html',
  styleUrls: ['./calendar-month.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarMonthComponent implements OnInit, OnChanges {
  isInitialized: boolean = false;
  DATE_FORMAT_ANGULAR = appSettings.DATE_FORMAT_ANGULAR;
  weeks: CalendarWeek[] = [];
  selectedDay: moment.Moment;
  month: moment.Moment;
  start: moment.Moment;
  flordersFound: boolean;
  calendarForm: UntypedFormGroup;

  date: string;

  @Input() days: DatesWithFlorderInfo;
  @Input() availableDays: AvailablePickingDates;
  @Input() headerMessage: string;

  @Output() showFlorderModal = new EventEmitter<CalendarDay>();
  @Output() loadFlorders = new EventEmitter<{ fromDate: string; toDate: string }>();
  @Output() openFlorder = new EventEmitter<CalendarFlorderInfo>();
  @Output() requestDeliveryDates = new EventEmitter();
  @Output() cdkDroplistChanged = new EventEmitter<CdkDropList[]>();

  @ContentChild(TemplateRef, { static: false }) tooltipRef: TemplateRef<any>;

  _mydate: any;
  @ViewChild('mydate', { static: false })
  set mydate(val: any) {
    this._mydate = val;
  }

  get mydate(): any {
    return this._mydate;
  }

  myDatePickerOptions;

  constructor(private fb: UntypedFormBuilder) {
  }

  ngOnInit(): void {
    this.isInitialized = true;
    this.initValues(true);
    this.createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.days && this.days.dates.length >= 0) {
      this.flordersFound = this.days.legend.length > 0;
      if (this.isInitialized) {
        this.initValues(false);
      }
    }

    if (this.availableDays && (this.availableDays.full.length > 0 || this.availableDays.nonFull.length > 0)) {
      // fill in calendar with available days
      this.initValues(false);
    }
  }

  protected initValues(loadFlorders: boolean) {
    this.selectedDay = this._removeTime(this.selectedDay || moment());
    this.month = this.selectedDay.clone();

    this.start = this.selectedDay.clone();
    this.start.date(-6);
    this._removeTime(this.start.day(0));

    this._buildMonth(this.start, this.month);

    if (loadFlorders) {
      // Only begin and end of current selected month
      // const fromDate = this.month.startOf('month').format(AppSettings.DATE_FORMAT_MOMENT_REST);
      // const toDate = this.month.endOf('month').format(AppSettings.DATE_FORMAT_MOMENT_REST);

      // Only begin and end of visible days on calendar
      const fromDate = this.weeks[0].days[0].date.format(appSettings.DATE_FORMAT_MOMENT_REST);
      const toDate = this.weeks[this.weeks.length - 1].days[6].date.format(appSettings.DATE_FORMAT_MOMENT_REST);
      this.loadFlorders.emit({ fromDate, toDate });
    }
  }

  _removeTime(date: moment.Moment): moment.Moment {
    return date.day(1).hour(0).minute(0).second(0).millisecond(0);
  }

  _buildMonth(start: moment.Moment, month: moment.Moment) {
    this.weeks = [];
    this._removeTime(start.endOf('month').day(0));
    let done = false;
    let date = start.clone();
    let monthIndex = date.month();
    let count = 0;
    while (!done) {
      const week = {
        days: [],
      };
      this.weeks.push(week);
      for (let i = 0; i < 7; i++) {
        const dayWithFlordersInfo = this.days.dates.find(day => date.isSame(moment(day.unloadingDate), 'day'));
        week.days.push({
          date,
          name: date.format('dd').substring(0, 1),
          number: date.date(),
          isCurrentMonth: date.month() === month.month(),
          isToday: date.isSame(new Date(), 'day'),
          isWeekend: date.day() === 6 || date.day() === 0,
          isFuture: date.diff(moment()) > 0,
          dateString: date.format(appSettings.DATE_FORMAT_MOMENT_REST),
          florderInfos: (dayWithFlordersInfo && dayWithFlordersInfo.florderInfos) || [], // TODO 452: enable
          isAvailableForDelivery: this.checkAvailableDay(date),
          totalNumberOfOrders: dayWithFlordersInfo && dayWithFlordersInfo.totalNumberOfOrders,
        } as CalendarDay);
        date = date.clone();
        date.add(1, 'd');
      }
      done = count++ > 2 && monthIndex !== date.month();
      monthIndex = date.month();
    }
  }

  private checkAvailableDay(date) {
    if (this.availableDays) {
      return this.availableDays.full.indexOf(date.format(appSettings.DATE_FORMAT_MOMENT_REST)) > -1;
    }
    return true;
  }

  private goToMonth(selectedMonth: number, year?: number) {
    this.month.month(selectedMonth);
    this.selectedDay.month(selectedMonth).endOf('month').subtract(14, 'days');

    year = year || this.selectedDay.year();
    this.month.year(year);
    this.selectedDay.year(year);

    // const fromDate = this.month.startOf('month').format(AppSettings.DATE_FORMAT_MOMENT_REST);
    // const toDate = this.month.endOf('month').format(AppSettings.DATE_FORMAT_MOMENT_REST);
    // this.loadOrders.emit({ florderDateFrom: fromDate, florderDateTo: toDate });
    this.initValues(true);

    // request delivery dates
    this.requestDeliveryDates.emit();
  }

  next() {
    this.goToMonth(this.month.month() + 1);
    this.date = null;
  }

  previous() {
    // prevent going to past months
    if (this.isCurrentMonthNotInPast()) {
      this.goToMonth(this.month.month() - 1);
      this.date = null;
    }
  }

  isCurrentMonthNotInPast() {
    return this.month.year() > moment().year() || (this.month.year() == moment().year() && this.month.month() > moment().month());
  }

  showToday() {
    this.date = moment().format(appSettings.DATE_FORMAT_MOMENT_REST);
    this.goToMonth(moment().month(), moment().year());
  }

  onDateChanged(dateStr: string) {
    const date = dateStr && moment(dateStr, appSettings.DATE_FORMAT_MOMENT_REST);
    if (date?.isValid()) {
      this.goToMonth(date.month(), date.year());
    }
  }

  private createForm() {
    this.calendarForm = this.fb.group({ calendarDate: [null] });
  }
}
